import React, { FC } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import {
  fmtDay,
  fmtInt,
  fmtMinutes,
  fmtPct,
  humanize,
  KIND_LABEL,
  languageLabel,
  NO_DATA,
} from './opsCapacityUtils';
import type {
  OpsCapacityConcentration,
  OpsCapacityCoverage,
  OpsCapacityLanguages,
  OpsCapacityQuality,
  OpsCapacitySpeed,
} from './types';
import { Empty, SimpleTable } from './OpsCapacitySection';

const GRID = 'rgba(148, 163, 184, 0.15)';

const barOpts: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { font: { size: 10 } },
    },
    y: { stacked: true, beginAtZero: true, grid: { color: GRID } },
  },
};

const lineOpts: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    y: { beginAtZero: true, grid: { color: GRID } },
  },
};

const PALETTE = [
  'rgb(59, 130, 246)',
  'rgb(16, 185, 129)',
  'rgb(245, 158, 11)',
  'rgb(168, 85, 247)',
  'rgb(239, 68, 68)',
  'rgb(14, 165, 233)',
  'rgb(132, 204, 22)',
  'rgb(148, 163, 184)',
];

const SHIFT_LABEL: Record<string, string> = {
  '0-8': '00:00–08:00',
  '8-16': '08:00–16:00',
  '16-21': '16:00–21:00',
  '21-24': '21:00–24:00',
};

// ─── Languages ──────────────────────────────────────────────────────────────

export const LanguagesSection: FC<{ languages: OpsCapacityLanguages }> = ({
  languages,
}) => {
  const { byLanguage, weekly, phoneCountries, structuredSharePct } = languages;
  if (byLanguage.length === 0)
    return <Empty>No phone tasks in this range.</Empty>;

  const weeks = Array.from(new Set(weekly.map((w) => w.weekStart))).sort();
  const topLanguages = byLanguage.slice(0, 6).map((l) => l.language);
  const weeklySeries = topLanguages.map((lang, i) => ({
    label: languageLabel(lang),
    data: weeks.map(
      (wk) =>
        weekly.find((w) => w.weekStart === wk && w.language === lang)?.count ??
        0,
    ),
    borderColor: PALETTE[i % PALETTE.length],
    backgroundColor: PALETTE[i % PALETTE.length],
    borderWidth: 2,
    pointRadius: 2,
    tension: 0.3,
  }));

  return (
    <div className='ops-grid ops-grid--2'>
      <div>
        <h4>Phone tasks by language</h4>
        <div className='ops-chart'>
          <Bar
            options={{
              ...barOpts,
              indexAxis: 'y',
              scales: {
                x: { stacked: true, beginAtZero: true, grid: { color: GRID } },
                y: { stacked: true, grid: { display: false } },
              },
            }}
            data={{
              labels: byLanguage.map((l) => languageLabel(l.language)),
              datasets: [
                {
                  label: 'Recorded on the task',
                  data: byLanguage.map((l) => l.fromStructured),
                  backgroundColor: 'rgb(59, 130, 246)',
                },
                {
                  label: 'Inferred from dialled country',
                  data: byLanguage.map((l) => l.fromPhoneCountry),
                  backgroundColor: 'rgba(59, 130, 246, 0.35)',
                },
                {
                  label: 'Unknown',
                  data: byLanguage.map(
                    (l) => l.count - l.fromStructured - l.fromPhoneCountry,
                  ),
                  backgroundColor: 'rgb(203, 213, 225)',
                },
              ],
            }}
          />
        </div>
        <p className='ops-chart-note'>
          {structuredSharePct == null
            ? 'No phone task in this range has a recorded language.'
            : `${fmtPct(structuredSharePct)} of phone tasks have a recorded language; the rest is inferred from the dialled country.`}
        </p>
      </div>
      <div>
        <h4>Language mix by week</h4>
        {weeks.length === 0 ? (
          <Empty />
        ) : (
          <div className='ops-chart'>
            <Line
              options={lineOpts}
              data={{ labels: weeks.map(fmtDay), datasets: weeklySeries }}
            />
          </div>
        )}
        {phoneCountries.length > 0 && (
          <SimpleTable
            caption='Dialled countries (phone tasks)'
            columns={[
              { key: 'country', label: 'Country' },
              { key: 'count', label: 'Tasks', align: 'right' },
            ]}
            rows={phoneCountries.slice(0, 8).map((c) => ({
              country: c.country === 'unknown' ? 'Unknown' : c.country,
              count: fmtInt(c.count),
            }))}
            rowKey={(r) => String(r.country)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Coverage ───────────────────────────────────────────────────────────────

export const CoverageSection: FC<{ coverage: OpsCapacityCoverage }> = ({
  coverage,
}) => {
  const { byShift, rotaCoveredDays, outOfHoursSharePct } = coverage;
  if (byShift.every((s) => s.tasks + s.interventions + s.mfa === 0)) {
    return <Empty />;
  }
  return (
    <div className='ops-grid ops-grid--2'>
      <div className='ops-chart'>
        <Bar
          options={barOpts}
          data={{
            labels: byShift.map((s) => SHIFT_LABEL[s.shift] ?? s.shift),
            datasets: [
              {
                label: 'Tasks with an operator rostered',
                data: byShift.map((s) => s.tasksRostered),
                backgroundColor: 'rgb(16, 185, 129)',
              },
              {
                label: 'Tasks with nobody rostered',
                data: byShift.map((s) => s.tasksUnrostered),
                backgroundColor: 'rgb(239, 68, 68)',
              },
              {
                label: 'Tasks on days without a rota',
                data: byShift.map(
                  (s) => s.tasks - s.tasksRostered - s.tasksUnrostered,
                ),
                backgroundColor: 'rgb(203, 213, 225)',
              },
            ],
          }}
        />
      </div>
      <div>
        <SimpleTable
          columns={[
            { key: 'shift', label: 'Shift (London)' },
            { key: 'tasks', label: 'Tasks', align: 'right' },
            { key: 'interventions', label: 'Interventions', align: 'right' },
            { key: 'mfa', label: 'MFA', align: 'right' },
            { key: 'p50', label: 'Pickup p50', align: 'right' },
            { key: 'p90', label: 'Pickup p90', align: 'right' },
          ]}
          rows={byShift.map((s) => ({
            shift: SHIFT_LABEL[s.shift] ?? s.shift,
            tasks: fmtInt(s.tasks),
            interventions: fmtInt(s.interventions),
            mfa: fmtInt(s.mfa),
            p50: fmtMinutes(s.pickupMedianMinutes),
            p90: fmtMinutes(s.pickupP90Minutes),
          }))}
          rowKey={(r) => String(r.shift)}
        />
        <p className='ops-chart-note'>
          {rotaCoveredDays} day{rotaCoveredDays === 1 ? '' : 's'} in the range
          have a rota entry.{' '}
          {outOfHoursSharePct != null &&
            `${fmtPct(outOfHoursSharePct)} of tasks arrived outside 08:00–21:00 London.`}
        </p>
      </div>
    </div>
  );
};

// ─── Speed ──────────────────────────────────────────────────────────────────

export const SpeedSection: FC<{ speed: OpsCapacitySpeed }> = ({ speed }) => {
  const { weekly, handleTime, cycleTime } = speed;
  const durationRows = (rows: OpsCapacitySpeed['handleTime']) =>
    rows
      .filter((r) => r.sampleSize > 0)
      .map((r) => ({
        kind: KIND_LABEL[r.kind],
        p50: fmtMinutes(r.medianMinutes),
        p90: fmtMinutes(r.p90Minutes),
        mean: fmtMinutes(r.meanMinutes),
        n: fmtInt(r.sampleSize),
      }));
  const durationColumns = [
    { key: 'kind', label: 'Stream' },
    { key: 'p50', label: 'Median', align: 'right' as const },
    { key: 'p90', label: 'p90', align: 'right' as const },
    { key: 'mean', label: 'Mean', align: 'right' as const },
    { key: 'n', label: 'Sample', align: 'right' as const },
  ];
  const handleRows = durationRows(handleTime);
  const cycleRows = durationRows(cycleTime);
  if (
    weekly.length === 0 &&
    handleRows.length === 0 &&
    cycleRows.length === 0
  ) {
    return <Empty />;
  }
  return (
    <div className='ops-grid ops-grid--2'>
      <div>
        <h4>Median wait before pickup, by week</h4>
        {weekly.length === 0 ? (
          <Empty />
        ) : (
          <div className='ops-chart'>
            <Line
              options={{
                ...lineOpts,
                scales: {
                  ...lineOpts.scales,
                  y: {
                    beginAtZero: true,
                    grid: { color: GRID },
                    title: {
                      display: true,
                      text: 'minutes',
                      font: { size: 10 },
                    },
                  },
                },
              }}
              data={{
                labels: weekly.map((w) => fmtDay(w.weekStart)),
                datasets: [
                  {
                    label: 'Phone pickup (median)',
                    data: weekly.map((w) => w.phonePickupMedianMinutes),
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgb(59, 130, 246)',
                    borderWidth: 2,
                    pointRadius: 2,
                    tension: 0.3,
                    spanGaps: true,
                  },
                  {
                    label: 'Web pickup (median)',
                    data: weekly.map((w) => w.webPickupMedianMinutes),
                    borderColor: 'rgb(16, 185, 129)',
                    backgroundColor: 'rgb(16, 185, 129)',
                    borderWidth: 2,
                    pointRadius: 2,
                    tension: 0.3,
                    spanGaps: true,
                  },
                  {
                    label: 'Intervention duration (median)',
                    data: weekly.map(
                      (w) => w.interventionDurationMedianMinutes,
                    ),
                    borderColor: 'rgb(245, 158, 11)',
                    backgroundColor: 'rgb(245, 158, 11)',
                    borderWidth: 2,
                    pointRadius: 2,
                    tension: 0.3,
                    spanGaps: true,
                  },
                ],
              }}
            />
          </div>
        )}
      </div>
      <div>
        <h4>Handle time (operator-reported)</h4>
        {handleRows.length === 0 ? (
          <Empty />
        ) : (
          <SimpleTable
            columns={durationColumns}
            rows={handleRows}
            rowKey={(r) => String(r.kind)}
          />
        )}
        <h4>Cycle time (created → completed)</h4>
        {cycleRows.length === 0 ? (
          <Empty />
        ) : (
          <SimpleTable
            columns={durationColumns}
            rows={cycleRows}
            rowKey={(r) => String(r.kind)}
          />
        )}
      </div>
    </div>
  );
};

// ─── Quality ────────────────────────────────────────────────────────────────

const CountList: FC<{
  title: string;
  rows: Array<{ key: string; count: number; extra?: string }>;
}> = ({ title, rows }) => {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <div className='ops-countlist'>
      <h4>{title}</h4>
      {rows.length === 0 ? (
        <Empty />
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r.key}>
              <span className='ops-countlist-key'>{humanize(r.key)}</span>
              <span className='ops-countlist-bar'>
                <span
                  style={{ width: `${total ? (100 * r.count) / total : 0}%` }}
                />
              </span>
              <span className='ops-countlist-count'>
                {fmtInt(r.count)}
                {r.extra ? ` · ${r.extra}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const QualitySection: FC<{ quality: OpsCapacityQuality }> = ({
  quality,
}) => (
  <>
    <div className='ops-kpi-inline'>
      <span>
        Cancellation rate{' '}
        <strong>{fmtPct(quality.cancellationRatePct, 1)}</strong>
      </span>
      <span>
        Tasks from new users{' '}
        <strong>{fmtPct(quality.newUserSharePct, 1)}</strong>
      </span>
    </div>
    <div className='ops-grid ops-grid--4'>
      <CountList title='Task status' rows={quality.statuses} />
      <CountList title='Close reasons' rows={quality.closeReasons} />
      <CountList
        title='Intervention outcomes'
        rows={quality.interventionOutcomes}
      />
      <CountList
        title='MFA outcomes'
        rows={quality.mfaOutcomes.map((m) => ({
          key: m.key,
          count: m.count,
          extra:
            m.medianResponseSeconds == null
              ? undefined
              : `median ${fmtMinutes(m.medianResponseSeconds / 60)}`,
        }))}
      />
    </div>
  </>
);

// ─── Concentration ──────────────────────────────────────────────────────────

export const ConcentrationSection: FC<{
  concentration: OpsCapacityConcentration;
}> = ({ concentration }) => {
  const { topRequesters, operators, top5RequesterSharePct } = concentration;
  if (topRequesters.length === 0 && operators.length === 0) return <Empty />;
  return (
    <div className='ops-grid ops-grid--2'>
      <div>
        <h4>
          Top requesters{' '}
          {top5RequesterSharePct != null && (
            <span className='ops-h4-meta'>
              top 5 = {fmtPct(top5RequesterSharePct)} of tasks
            </span>
          )}
        </h4>
        {topRequesters.length === 0 ? (
          <Empty />
        ) : (
          <SimpleTable
            columns={[
              { key: 'name', label: 'User' },
              { key: 'phone', label: 'Phone', align: 'right' },
              { key: 'web', label: 'Web', align: 'right' },
              { key: 'total', label: 'Total', align: 'right' },
            ]}
            rows={topRequesters.map((r) => ({
              id: r.userId,
              name: r.name ?? r.userId,
              phone: fmtInt(r.phone),
              web: fmtInt(r.web),
              total: fmtInt(r.total),
            }))}
            rowKey={(r) => String(r.id)}
          />
        )}
      </div>
      <div>
        <h4>Operators</h4>
        {operators.length === 0 ? (
          <Empty>No task was accepted in this range.</Empty>
        ) : (
          <SimpleTable
            columns={[
              { key: 'name', label: 'Operator' },
              { key: 'accepted', label: 'Tasks accepted', align: 'right' },
              { key: 'handle', label: 'Median handle', align: 'right' },
            ]}
            rows={operators.map((o) => ({
              id: o.userId,
              name: o.name ?? o.userId,
              accepted: fmtInt(o.tasksAccepted),
              handle:
                o.medianHandleMinutes == null
                  ? NO_DATA
                  : fmtMinutes(o.medianHandleMinutes),
            }))}
            rowKey={(r) => String(r.id)}
          />
        )}
      </div>
    </div>
  );
};
